import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import Header from 'widgets/header';
import { getDomainOrigin, getAxiosConfig } from 'utils/serverConfig';
import {
    Wrapper,
    HeaderStyles,
    BackArrowIcon,
    Title,
    Text,
    RowWrapper,
} from './styles';
import Image from 'next/image';
import { getImageBasePath } from 'utils/generics';
import { MPosContext } from 'utils/context';
import { PageLoader } from 'utils/styles';
import ErrorPage from 'widgets/errorScreen';
import { mapFormConfigs } from 'components/login/helpers';
import { FORM_TYPES } from 'utils/constants';
import {
    SegmentEventActionNames,
    SegmentEventButtonNames,
    SegmentEventNameTypes,
} from 'utils/segment/constants';
import sendSegmentEvent from 'utils/segment';
import { useResponsiveDevice } from 'hooks/useResponsiveDevice';

interface IFieldProps {
    editable?: boolean;
    key_name: string;
    maxLength?: number;
}

interface IFieldConfig {
    id: string;
    type: string;
    label: string;
    props: IFieldProps;
    required?: boolean;
    component: string;
    title?: string;
}

interface IFormResponse {
    form_type: string;
    ui_config: IFieldConfig[];
    captured_data?: Record<string, any>;
}

function Details() {
    const [data, setData] = useState<IFormResponse[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const {
        setFormConfigsMap,
        setShowHamburger,
        formConfigsMap,
        permissions,
        userDetails,
    } = useContext(MPosContext);
    const { isMobile } = useResponsiveDevice();
    const onBack = () => {
        // Segment Event
        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            {
                button_name: SegmentEventButtonNames.DETAILS_BACK_BUTTON,
                action_name: SegmentEventActionNames.CLICK_BACK_BUTTON,
            },
            { userDetails, isMobile }
        );

        setShowHamburger(true);
    };

    useEffect(() => {
        // If dealer_details config with captured_data is already present, skip API call
        const dealerFromContext = formConfigsMap[FORM_TYPES.DEALER_DETAILS];
        const hasCapturedData =
            dealerFromContext &&
            dealerFromContext.captured_data &&
            Object.keys(dealerFromContext.captured_data).length > 0;

        if (hasCapturedData) {
            setIsLoading(false);
            return;
        }

        const fetchDetails = async () => {
            try {
                const { status, data } = await axios({
                    method: 'GET',
                    url: `${getDomainOrigin()}/api/mpos/genie/onboarding/${
                        permissions?.partner_id
                    }/details/get-data?user_group=${permissions?.group}`,
                    ...getAxiosConfig(),
                });

                if (status === 200 && Array.isArray(data)) {
                    setData(data);
                    const newFormConfigsMap = mapFormConfigs(data);
                    setFormConfigsMap(newFormConfigsMap);
                } else {
                    setError('Something went wrong while fetching details.');
                }
            } catch (e: any) {
                setError(
                    e?.response?.data?.message ||
                        'Something went wrong while fetching details.'
                );
            } finally {
                setIsLoading(false);
            }
        };

        fetchDetails();
    }, []);

    if (isLoading) {
        return <PageLoader src={getImageBasePath('/gif/pageLoader.gif')} />;
    }

    if (error) {
        return <ErrorPage errorMessage={error} />;
    }

    const dealerFormFromContext = formConfigsMap[FORM_TYPES.DEALER_DETAILS];
    const dealerFormFromApi = data.find(
        (form) => form.form_type === 'dealer_details'
    );

    const uiConfig =
        dealerFormFromContext?.uiConfig || dealerFormFromApi?.ui_config || [];
    const capturedData =
        dealerFormFromContext?.captured_data ||
        dealerFormFromApi?.captured_data ||
        {};

    return (
        <Wrapper>
            <Header />
            <HeaderStyles>
                <BackArrowIcon>
                    <Image
                        width={'10px'}
                        height={'24px'}
                        alt={''}
                        src={getImageBasePath('/icons/backArrow.svg')}
                        onClick={onBack}
                    />
                </BackArrowIcon>
            </HeaderStyles>
            <Title>Details</Title>
            <Text>
                {uiConfig.map((field) => {
                    const keyName = field.props?.key_name;
                    const value = capturedData[keyName] ?? '-';

                    return (
                        <RowWrapper key={field.id}>
                            <span>{field.label}</span>
                            <span>{value}</span>
                        </RowWrapper>
                    );
                })}
            </Text>
        </Wrapper>
    );
}

export default Details;
